import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convertit une chaîne de temps "HH:mm:ss" en secondes depuis minuit.
 */
export function timeToSeconds(time: string): number {
  // Garde pour les chaînes vides ou invalides, pour éviter les erreurs NaN.
  if (!time || !/^\d{2}:\d{2}(:\d{2})?$/.test(time)) {
    return 0;
  }
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + (seconds || 0);
}

export function getTimeDifferenceInSeconds(time1: string, time2: string): number {
  return Math.abs(timeToSeconds(time1) - timeToSeconds(time2));
}

/**
 * Ajoute un nombre de secondes à une chaîne de temps "HH:mm:ss".
 */
export function addSecondsToTime(time: string, secondsToAdd: number): string {
  const initialSeconds = timeToSeconds(time);
  const newTotalSeconds = initialSeconds + secondsToAdd;
  return formatSecondsToHHMMSS(newTotalSeconds);
}

/**
 * Convertit un nombre de secondes depuis minuit en une chaîne "HH:mm:ss".
 */
export function formatSecondsToHHMMSS(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    String(hours).padStart(2, '0'),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0')
  ].join(':');
}

const dayOfWeekMap: { [key: number]: string } = {
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
  7: "Dim",
};

/**
 * Formate un tableau de jours de la semaine en une chaîne lisible.
 * ex: [1, 2, 5] => "Lun, Mar, Ven"
 */
export function formatDaysOfWeek(days: number[]): string {
  if (!days || days.length === 0) return "Aucun";
  if (days.length === 7) return "Tous les jours";

  return days
      .sort((a, b) => a - b)
      .map(day => dayOfWeekMap[day] || '')
      .join(', ');
}

/**
 * Convertit une durée en secondes en une chaîne de format mm:ss.
 * @param totalSeconds - Le nombre total de secondes.
 * @returns Une chaîne formatée, par exemple "05:30".
 */
export function formatSecondsToMMSS(totalSeconds: number): string {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return "00:00";
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}


/**
 * Analyse une chaîne de durée ISO 8601 (simplifiée) et la retourne en secondes.
 * Gère les formats comme "PT5M30S".
 * @param isoDuration - La chaîne de durée ISO 8601.
 * @returns Le nombre total de secondes.
 */
export function parseISODurationToSeconds(isoDuration: string | null | undefined): number {
  if (!isoDuration) return 0;

  const matches = isoDuration.match(/PT(?:(\d+)M)?(?:(\d+)S)?/);
  if (!matches) return 0;

  const minutes = matches[1] ? parseInt(matches[1], 10) : 0;
  const seconds = matches[2] ? parseInt(matches[2], 10) : 0;

  return (minutes * 60) + seconds;
}

/**
 * Analyse une entrée utilisateur (ex: "5", "5:30", "330s") et la convertit en secondes.
 * @param input - La chaîne entrée par l'utilisateur.
 * @returns Le nombre total de secondes.
 */
export function parseTimeInputToSeconds(input: string): number {
  if (!input) return 0;

  // Cas "5:30"
  if (input.includes(':')) {
    const parts = input.split(':');
    const minutes = parseInt(parts[0], 10) || 0;
    const seconds = parseInt(parts[1], 10) || 0;
    return (minutes * 60) + seconds;
  }

  // Cas "330s"
  if (input.toLowerCase().endsWith('s')) {
    return parseInt(input, 10) || 0;
  }

  // Cas "5" (on suppose des minutes)
  const asNumber = parseInt(input, 10);
  if (!isNaN(asNumber)) {
    return asNumber * 60;
  }

  return 0;
}

/**
 * Convertit un nombre de secondes en une chaîne de durée ISO 8601 (simplifiée).
 * @param totalSeconds - Le nombre total de secondes.
 * @returns Une chaîne de durée ISO 8601, par exemple "PT5M30S".
 */
export function formatSecondsToISODuration(totalSeconds: number): string {
  if (isNaN(totalSeconds) || totalSeconds <= 0) {
    return "PT0S";
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  let duration = "PT";
  if (minutes > 0) {
    duration += `${minutes}M`;
  }
  if (seconds > 0 || minutes === 0) {
    duration += `${seconds}S`;
  }
  return duration;
}


/**
 * Convertit un nombre de minutes en chaîne de durée ISO 8601.
 * @param minutes - Le nombre de minutes.
 * @returns Une chaîne de durée ISO 8601, par exemple "PT15M".
 */
export function formatMinutesToISODuration(minutes: number): string {
  if (isNaN(minutes) || minutes <= 0) {
    return "PT0M";
  }
  return `PT${minutes}M`;
}

/**
 * Analyse une chaîne de durée ISO 8601 et retourne le nombre de minutes.
 * Gère les formats comme "PT15M".
 * @param isoDuration - La chaîne de durée ISO 8601.
 * @returns Le nombre de minutes.
 */
export function parseISODurationToMinutes(isoDuration: string | null | undefined): number {
  if (!isoDuration) return 0;
  const match = isoDuration.match(/PT(\d+)M/);
  return match ? parseInt(match[1], 10) : 0;
}

// --- ✨ NOUVELLES FONCTIONS pour le formatage d'heure et de date ---

/**
 * Formate une heure (ex: "14:30:00") en un format plus court "HH:mm".
 */
export function formatTimeToHHMM(time: string | null | undefined): string {
  if (!time) return '';
  return time.substring(0, 5);
}





export interface Page<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number; // numéro de page actuel (0-indexed)
  sort: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export function formatDate(dateString: string | Date): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-CA'); // Or any other format like 'dd/MM/yyyy'
}

